import {
    isValidElement,
    type ReactNode,
    Children,
    useMemo,
    type FC
} from "react";
import {
    View
} from "react-native";
import type IMessageGroupProps from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import type {
    MessageGroupContextType,
    MessageGroupPosition
} from "../../type";
import {
    formatNumber
} from "../../../../helpers/locale";
import {
    MessageGroupContext
} from "../../context";

const MessageGroupProvider: FC<MessageGroupContextType & {
    children: ReactNode;
}> = ({
    customLocalize,
    customTheme,
    children,
    position,
    avatar,
    align,
    title
}) => {
    const value = useMemo(() => ({
        customLocalize,
        customTheme,
        position,
        avatar,
        align,
        title
    }), [
        customLocalize,
        customTheme,
        position,
        avatar,
        align,
        title
    ]);

    return <MessageGroupContext.Provider
        value={value}
    >
        {children}
    </MessageGroupContext.Provider>;
};

const MessageGroup: FC<IMessageGroupProps> = ({
    align = "start",
    customLocalize,
    customTheme,
    children,
    avatar,
    style,
    title,
    ...props
}) => {
    const {
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        activeLocale,
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const {
        container: containerDynamicStyle
    } = useStyles({
        spaces
    });

    const items = Children.toArray(children).filter(isValidElement);

    const getPosition = (index: number): MessageGroupPosition => {
        if(items.length === 1) {
            return "single";
        }

        if(index === 0) {
            return "first";
        }

        return index === items.length - 1 ? "last" : "middle";
    };

    return <View
        {...props}
        aria-label={title ? localize("message-group-label", [
            title,
            formatNumber({
                locale: activeLocale,
                value: items.length
            })
        ]) : undefined}
        style={[
            style,
            stylesheet.container,
            containerDynamicStyle
        ]}
        role="group"
    >
        {
            items.map((item, index) => <MessageGroupProvider
                customLocalize={customLocalize}
                position={getPosition(index)}
                customTheme={customTheme}
                key={item.key ?? index}
                avatar={avatar}
                align={align}
                title={title}
            >
                {item}
            </MessageGroupProvider>)
        }
    </View>;
};
export default MessageGroup;
